// composeApp/src/commonMain/kotlin/dk/smark/demo/ui/LoginScreen.kt
package dk.smark.chat.ui

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import dk.smark.chat.MainViewModel
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LoginScreen(
    onLoginSuccess: () -> Unit,
    startOnRegister: Boolean = false,
    viewModel: MainViewModel = koinViewModel()
) {
    var isRegistering by remember { mutableStateOf(startOnRegister) }
    var isForgotPassword by remember { mutableStateOf(false) }

    when {
        isRegistering -> RegisterForm(
            onRegistered = { isRegistering = false },
            onBackToLogin = { isRegistering = false },
            viewModel = viewModel
        )
        isForgotPassword -> ForgotPasswordForm(
            onBack = { isForgotPassword = false },
            viewModel = viewModel
        )
        else -> LoginForm(
            onLoginSuccess = onLoginSuccess,
            onCreateAccount = { isRegistering = true },
            onForgotPassword = { isForgotPassword = true },
            viewModel = viewModel
        )
    }
}

@Composable
private fun LoginForm(
    onLoginSuccess: () -> Unit,
    onCreateAccount: () -> Unit,
    onForgotPassword: () -> Unit,
    viewModel: MainViewModel
) {
    val saved = remember { viewModel.getSavedCredentials() }
    var email by remember { mutableStateOf(saved?.email ?: "") }
    var password by remember { mutableStateOf(saved?.password ?: "") }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    var passwordVisible by remember { mutableStateOf(false) }
    var rememberMe by remember { mutableStateOf(saved != null) }

    Column(
        modifier = Modifier.fillMaxSize().padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text("Login", style = MaterialTheme.typography.headlineMedium)
        Spacer(modifier = Modifier.height(24.dp))

        OutlinedTextField(
            value = email,
            onValueChange = { email = it },
            label = { Text("Email") },
            singleLine = true,
            modifier = Modifier.fillMaxWidth()
        )
        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = password,
            onValueChange = { password = it },
            label = { Text("Password") },
            singleLine = true,
            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
            trailingIcon = {
                TextButton(onClick = { passwordVisible = !passwordVisible }) {
                    Text(if (passwordVisible) "Hide" else "Show")
                }
            },
            modifier = Modifier.fillMaxWidth()
        )
        Spacer(modifier = Modifier.height(8.dp))

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth()
        ) {
            Checkbox(
                checked = rememberMe,
                onCheckedChange = { rememberMe = it }
            )
            Text("Remember me")
        }
        Spacer(modifier = Modifier.height(8.dp))

        errorMessage?.let {
            Text(it, color = MaterialTheme.colorScheme.error)
            Spacer(modifier = Modifier.height(8.dp))
        }

        Button(
            onClick = {
                isLoading = true
                errorMessage = null
                viewModel.login(email, password, rememberMe,
                    onSuccess = { isLoading = false; onLoginSuccess() },
                    onError = { isLoading = false; errorMessage = it }
                )
            },
            enabled = !isLoading,
            modifier = Modifier.fillMaxWidth()
        ) {
            if (isLoading) CircularProgressIndicator(modifier = Modifier.size(20.dp))
            else Text(text = "Login")
        }

        Spacer(modifier = Modifier.height(12.dp))

        TextButton(onClick = onForgotPassword) {
            Text("Forgot password?")
        }

        TextButton(onClick = onCreateAccount) {
            Text("Create account")
        }
    }
}

@Composable
private fun ForgotPasswordForm(
    onBack: () -> Unit,
    viewModel: MainViewModel
) {
    var email by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var submitted by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxSize().padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text("Reset password", style = MaterialTheme.typography.headlineMedium)
        Spacer(modifier = Modifier.height(8.dp))

        if (submitted) {
            Text(
                "If an account exists for $email, you will receive a password reset email shortly.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(24.dp))
            TextButton(onClick = onBack) { Text("Back to login") }
        } else {
            Text(
                "Enter your email address and we'll send you a link to reset your password.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(24.dp))

            OutlinedTextField(
                value = email,
                onValueChange = { email = it },
                label = { Text("Email") },
                singleLine = true,
                modifier = Modifier.fillMaxWidth()
            )
            Spacer(modifier = Modifier.height(8.dp))

            errorMessage?.let {
                Text(it, color = MaterialTheme.colorScheme.error)
                Spacer(modifier = Modifier.height(8.dp))
            }

            Button(
                onClick = {
                    isLoading = true
                    errorMessage = null
                    viewModel.forgotPassword(email,
                        onSuccess = { isLoading = false; submitted = true },
                        onError = { isLoading = false; errorMessage = it }
                    )
                },
                enabled = !isLoading && email.isNotBlank(),
                modifier = Modifier.fillMaxWidth()
            ) {
                if (isLoading) CircularProgressIndicator(modifier = Modifier.size(20.dp))
                else Text("Send reset link")
            }

            Spacer(modifier = Modifier.height(12.dp))
            TextButton(onClick = onBack) { Text("Back to login") }
        }
    }
}

